import React, { useState } from 'react';
import UserModal from './users/UserModal';
import TrafficModal from './users/TrafficModal';
import Input from './Input';
import Loading from './Loading';
import { useConfirm } from './ConfirmContext';
import Table from './Table';
import Widget from './users/Widget';

import { ReactComponent as PlusIcon } from './images/plus.svg';
import { ReactComponent as ExclamationIcon } from './images/exclamation.svg';

const Users = ({users, setUsers}) => {
  const confirm = useConfirm();
  let [searchTerm, setSearchTerm] = useState('');
  let [editedUser, setEditedUser] = useState(null);
  let [userModalVisible, setUserModalVisible] = useState(false);
  let [trafficModalVisible, setTrafficModalVisible] = useState(false);

  const userMatches = (user, searchTerm) => {
    const lowerSearch = searchTerm.toLowerCase();

    return user.name.toLowerCase().includes(lowerSearch) ||
      user.company.toLowerCase().includes(lowerSearch) ||
      user.email.toLowerCase().includes(lowerSearch) ||
      user.websites.some(site => site.domain.toLowerCase().includes(lowerSearch));
  }

  const saveUser = (index, newUser) => {
    let newUsers;
    let id = null;

    // The rows below are updated in place, so hold on to the old value. The
    // list is updated optimistically and a failed save has to put it back.
    const previousSubscription = index === undefined
          ? null
          : users[index].subscription;

    if (index !== undefined) {
      id = users[index].id;
      newUsers = users.map((user, i) => {
        if (i === index) {
          let u = user;
          u.name = newUser.name;
          u.email = newUser.email;
          u.company = newUser.company;
          u.telephone = newUser.telephone;
          u.subscription = newUser.subscription;
          u.websites = newUser.websites;
          u.extraUsers = newUser.extraUsers;
          u.stripeCustomerId = newUser.stripeCustomerId;
          u.passwordOverwritten = newUser.passwordOverwritten;
          u.trying = newUser.trying;

          if (newUser.password !== '') {
            u.password = newUser.password;
            u.resetPassword = true;
          }
          return u;
        }
        return user;
      });
    }
    else {
      newUsers = [...users, {...newUser,
                             resetPassword: true,
                             index: users.length}];
      index = users.length;
    }

    setUsers(newUsers);

    fetch(process.env.REACT_APP_SERVER_URL + '/api/set-user', {
      method: 'POST',
      headers: {
        'Authorization': localStorage.getItem('token'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user: newUsers[index],
        id,
      }),
    })
      .then(response => response.json())
      .then(data => {
        if (!data.success) {
          revertSubscription();
          confirm(data.error || data.message || 'Could not save this user.', true);
          return;
        }

        setUsers(newUsers.map((user, i) => {
          if (i === index) {
            user.websites = data.newWebsites;
            user.extraUsers = data.newExtraUsers;
            user.id = data.id;
            return user;
          }
          return user;
        }));

        reportCancellation(data.legacyCancellation);
      })
      .catch(error => {
        console.log(error);
        revertSubscription();
        confirm('Could not reach the server, this user was not saved.', true);
      });

    // Put the subscription column back to what the server still holds, so a
    // failed save does not leave the list claiming a change that never landed.
    function revertSubscription () {
      if (previousSubscription === null) return;

      setUsers(newUsers.map((user, i) => {
        if (i === index) {
          user.subscription = previousSubscription;
          return user;
        }
        return user;
      }));
    }

    // Only set when the account was on the old billing system, where
    // cancelling means cancelling in Stripe rather than writing a row.
    function reportCancellation (cancellation) {
      if (!cancellation) return;

      if (cancellation.error) {
        revertSubscription();
        confirm('Stripe could not be reached, so this account is still ' +
                'active and will keep being billed. Everything else was ' +
                'saved. Please try cancelling again.', true);
      }
      else if (cancellation.canceled.length > 0) {
        const count = cancellation.canceled.length;
        confirm(`Cancelled ${count} Stripe subscription` +
                `${count === 1 ? '' : 's'} for this account.`, true);
      }
      else if (cancellation.rows > 0) {
        confirm('Cancelled this account. It had no Stripe subscription, ' +
                'only a subscription record.', true);
      }
      else {
        confirm('This account had nothing left to cancel.', true);
      }
    }
  }

  const deleteUser = async (index) => {
    const confirmed = await confirm(`Are you sure you want to delete user ${users[index].name}?`);
    if (confirmed) {
      let id = users[index].id;
      setUsers(users.filter((user, i) => i !== index));

      fetch(process.env.REACT_APP_SERVER_URL + '/api/delete-user', {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id }),
      })
        .catch(error => console.log(error));
    }
  }

  if (users === null) return <Loading />;

  const computeContents = (users) => {
    return users
      .filter(user => userMatches(user, searchTerm))
      .slice()
      .reverse()
      .map(user => {
        let status = '';

        if (!user.stripeCustomerId) {
          status = <ExclamationIcon
                     style={{width: '20px',
                             height: '20px',
                             fill: 'red',
                             cursor: 'pointer',
                            }}
                   />;
        }

        return [status,
                user.name,
                user.email,
                user.company,
                user.telephone,
                user.subscription,
                <Widget showTraffic={() => {
                          setEditedUser(user);
                          setTrafficModalVisible(true);
                        }}
                        editUser={() => {
                          setEditedUser(user);
                          setUserModalVisible(true);
                        }}
                        deleteUser={() => deleteUser(user.index)} />
               ];
      });
  }

  return (
    <div style={{width: 'calc(100% - 40px)',
                 overflowY: 'auto',
                 padding: '20px',
                 display: 'flex',
                 flexDirection: 'column',
                 gap: '20px',
                }}>
      {userModalVisible && <UserModal setVisible={setUserModalVisible}
                                      saveUser={saveUser}
                                      user={editedUser} />}

      {trafficModalVisible && <TrafficModal setVisible={setTrafficModalVisible}
                                            user={editedUser} />}

      <div style={{display: 'flex',
                   justifyContent: 'space-between',
                   alignItems: 'center',
                   gap: '20px',
                  }}>
        <Input type='text'
               placeholder='Search...'
               style={{width: '300px',
                       height: '20px',
                       flex: 1,
                       borderRadius: '5px',
                       padding: '10px',
                       backgroundColor: '#333',
                       color: 'white',
                       border: 'none',
                      }}
               onChange={(e) => setSearchTerm(e.target.value)}
               value={searchTerm} />
        <PlusIcon className='image'
                  style={{width: '20px',
                          height: '20px',
                          fill: 'white',
                          cursor: 'pointer',
                         }}
                  onClick={() => {
                    setEditedUser(null);
                    setUserModalVisible(true)
                  }} />
      </div>
      <Table headers={['', 'Name', 'Email', 'Company', 'Telephone', 'Subscription', '']}
             contents={computeContents(users)}
             proportions={[0.2, 1, 2, 1, 1, 1, 1]} />
    </div>
  );
}

export default Users;
